<template>
  <v-navigation-drawer
    class="app-drawer"
    :clipped="false"
    :mini-variant.sync="isMini"
    permanent
    app
    overflow
    dark
  >
    <v-list-item class="company-header" :class="{ 'px-2': isMini }">
      <img
        v-if="!isMini && logoSRC && !noLargeLogo"
        :src="logoSRC"
        class="logo-img"
        @error="noLargeLogo = true"
      />
      <img
        v-else-if="isMini && smallLogoSRC && !noSmallLogo"
        :src="smallLogoSRC"
        class="logo-img"
        @error="noSmallLogo = true"
      />
      <v-img
        v-else-if="isMini && !noSmallLogo"
        class="logo-img"
        :src="require('@/assets/logo_mini.png')"
        contain
      ></v-img>
      <v-img
        v-else
        class="logo-img"
        :src="require('@/assets/logo.png')"
        contain
      ></v-img>
    </v-list-item>
    <v-list dense class="padded">
      <div v-for="(group, idx) in items" :key="idx">
        <v-divider v-if="idx > 0" class="padded"></v-divider>
        <v-list-item
          v-for="(item, idx) in group.items"
          :key="idx"
          :to="{ name: item.routeName }"
          :disabled="item.disabled || item.locked"
        >
          <v-tooltip right :disabled="!isMini">
            <template v-slot:activator="{ on, attrs }">
              <v-list-item-icon v-if="item.icon" v-bind="attrs" v-on="on">
                <drawer-icon :name="item.icon"></drawer-icon>
              </v-list-item-icon>
            </template>
            <span>{{ item.title }}</span>
          </v-tooltip>

          <v-list-item-content class="nav-item">
            <v-list-item-title class="nav-title">{{
              item.title
            }}</v-list-item-title>
          </v-list-item-content>
          <v-icon v-if="item.disabled">mdi-hammer</v-icon>
          <v-icon v-else-if="item.locked">mdi-lock-outline</v-icon>
          <v-icon v-else>mdi-chevron-right</v-icon>
        </v-list-item>
      </div>
    </v-list>
    <v-divider v-if="!smallScreen" style="margin: 20px 0px 60px"></v-divider>
    <v-btn
      v-if="!smallScreen"
      absolute
      depressed
      @click.stop="mini = !mini"
      class="collapse-icon"
    >
      <v-img
        src="@/assets/images/ic-dash-max@3x.png"
        width="36"
        height="28"
        contain
      ></v-img>
    </v-btn>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from 'vuex'
import { getInitials } from '@/common'
import { getPublicImageURL } from '@/firebaseConfig'
import DrawerIcon from '@/components/DrawerIcon.vue'
import {
  hasAccessRightForCollection,
  AccessRightRead,
  canAccessWarehouse,
  canAccessPeople,
} from '@/plugins/feature'

export default {
  components: { DrawerIcon },
  data() {
    return {
      noLargeLogo: false,
      noSmallLogo: false,
      windowWidth: window.innerWidth,
      mini: false,
      items: [
        {
          items: [
            {
              title: 'Dashboard',
              routeName: 'dashboard',
              icon: 'ic-nav-dashboard',
              disabled: true,
            },
            {
              title: 'Roster',
              routeName: 'roster',
              icon: 'ic-nav-roster',
              disabled: true,
            },
          ],
        },
        {
          items: [
            {
              title: 'People',
              routeName: 'people',
              icon: 'ic-nav-people',
              locked: !canAccessPeople(),
            },
            {
              title: 'Holidays',
              routeName: 'holidays',
              icon: 'ic-nav-holidays',
              disabled: true,
            },
          ],
        },
        {
          items: [
            {
              title: 'Projects',
              routeName: 'projects',
              icon: 'ic-nav-projects',
              locked: !hasAccessRightForCollection('projects', AccessRightRead),
            },
            {
              title: 'Tasks',
              routeName: 'jobs',
              icon: 'ic-nav-jobs',
              locked: !hasAccessRightForCollection('jobs', AccessRightRead),
            },
            {
              title: 'Clients',
              routeName: 'clients',
              icon: 'ic-nav-clients',
              disabled: true,
            },
          ],
        },
        {
          items: [
            {
              title: 'Warehouse',
              routeName: 'warehouse',
              icon: 'ic-nav-warehouse',
              locked: !canAccessWarehouse(),
            },
          ],
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['currentUser', 'currentCompany']),
    companyInitials() {
      return getInitials(this.currentCompany?.name)
    },
    subdomain() {
      if (location.host.split('.').length > 2) {
        return location.host.split('.')[0]
      } else {
        this.currentCompany?.subdomain ?? undefined
      }
      return undefined
    },
    logoSRC() {
      if (this.subdomain) {
        return getPublicImageURL(this.subdomain, 'logo.png')
      }
      return null
    },
    smallLogoSRC() {
      if (this.subdomain) {
        return getPublicImageURL(this.subdomain, 'logo_mini.png')
      }
      return null
    },
    companyName() {
      if (this.currentCompany != null) {
        return this.isMini
          ? getInitials(this.currentCompany.name)
          : this.currentCompany.name
      } else {
        return ''
      }
    },
    smallScreen() {
      return this.windowWidth < 700
    },
    isMini() {
      return this.mini || this.smallScreen
    },
  },
  watch: {
    windowWidth(newWidth, oldWidth) {
      this.txt = `it changed to ${newWidth} from ${oldWidth}`
    },
  },

  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize)
    })
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },

  methods: {
    onResize() {
      this.windowWidth = window.innerWidth
    },
  },
}
</script>

<style lang="sass" scoped>
.app-drawer
  background: $secondary

.nav-item
  position: relative

.padded
  margin: 20px 0px

.company-header
  height: 84px
  background: $tertiary

.logo-img
  max-width: 100%
  max-height: 60px
  padding: 4px

.nav-title
  color: $foreground
  font-family: Montserrat
  font-size: 14px !important
  font-weight: 100 !important

.collapse-icon
  width: 100%
  bottom: 0
  min-width: 0px !important
  padding: 30px 0px 30px !important
  background-color: #292a2b !important

.v-list .v-icon
  color: $foreground

.v-list-item--active
  color: $primary

.v-list-item--disabled
  opacity: 0.2

.v-list-item--active::before
  opacity: 1
</style>
